/**
 * Installing a plugin (the plugin browser's Install… panel): by folder path (a core with a disk
 * reads it), Browse… (desktop app), or by choosing the plugin's files (read by the page, works
 * everywhere); dropping them on the window works too (`PluginsWindow.tsx`).
 */
import { createSignal, Show, type JSX } from "solid-js";
import { useApp } from "../../app/context";

/** The install panel of the Plugins window. */
export function InstallSection(): JSX.Element {
  const { plugins } = useApp();
  const [path, setPath] = createSignal("");
  let folderInput!: HTMLInputElement;
  let filesInput!: HTMLInputElement;

  /** Installs from the typed path, and clears it when that worked. */
  const install = async () => {
    if (await plugins.installFolder(path())) setPath("");
  };
  /** Installs from an input's files, then clears the input so the same files can be chosen again. */
  const fromInput = (input: HTMLInputElement) => {
    if (input.files?.length) void plugins.installFiles(input.files);
    input.value = "";
  };

  return (
    <section class="grp pbrowse-install" id="plugins-install">
      <h3>Install a plugin</h3>
      <Show when={plugins.readsFolders}>
        <div class="row-inline">
          <input
            class="field mono"
            id="plugins-install-path"
            placeholder="/path/to/plugin (the folder with plugin.kdl)"
            value={path()}
            onInput={(e) => setPath(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && void install()}
          />
          <button
            type="button"
            class="btn primary"
            id="plugins-install-folder"
            disabled={plugins.busy() || !path().trim()}
            onClick={() => void install()}
          >
            Install
          </button>
          <Show when={plugins.browse}>
            <button
              type="button"
              class="btn"
              id="plugins-browse"
              disabled={plugins.busy()}
              onClick={() => void plugins.browse!()}
            >
              Browse…
            </button>
          </Show>
        </div>
      </Show>
      <input
        type="file"
        hidden
        id="plugins-folder-input"
        {...{ webkitdirectory: true }}
        multiple
        ref={folderInput}
        onChange={(e) => fromInput(e.currentTarget)}
      />
      <input
        type="file"
        hidden
        id="plugins-files-input"
        accept=".kdl,.wasm"
        multiple
        ref={filesInput}
        onChange={(e) => fromInput(e.currentTarget)}
      />
      <div class="btnrow">
        <button
          type="button"
          class={plugins.readsFolders ? "btn" : "btn primary"}
          id="plugins-choose-folder"
          disabled={plugins.busy()}
          onClick={() => folderInput.click()}
        >
          Choose plugin folder…
        </button>
        <button
          type="button"
          class="btn"
          id="plugins-install-files"
          disabled={plugins.busy()}
          onClick={() => filesInput.click()}
        >
          Choose files…
        </button>
      </div>
      <p class="small muted">
        Or drop a plugin's plugin.kdl and .wasm file here. Installing a plugin
        with the id of an installed one updates it.
      </p>
      <Show when={plugins.error()}>
        <p class="small err" data-plugins-error>
          {plugins.error()}
        </p>
      </Show>
    </section>
  );
}
